import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { BUSINESS, PAGE_META } from '../constants/business';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

const SERVICE_OPTIONS = [
  { value: 'residential-closings', label: 'Residential Closings' },
  { value: 'commercial-closings', label: 'Commercial Closings' },
  { value: 'title-insurance', label: 'Title Insurance' },
  { value: 'title-search', label: 'Title Search' },
  { value: 'refinance', label: 'Refinance' },
  { value: 'other', label: 'Other' },
];

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    company: '', // honeypot
  });
  const [formState, setFormState] = useState<FormState>('idle');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validate = (field: string, value: string): string => {
    switch (field) {
      case 'name':
        return value.trim().length < 2 ? 'Name must be at least 2 characters.' : '';
      case 'email':
        return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ? 'Please enter a valid email address.' : '';
      case 'phone':
        return value.trim().length < 7 ? 'Please enter a valid phone number.' : '';
      case 'service':
        return !value ? 'Please select a service.' : '';
      case 'message':
        return value.trim().length < 10 ? 'Message must be at least 10 characters.' : '';
      default:
        return '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: validate(name, value) }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setFieldErrors(prev => ({ ...prev, [name]: validate(name, value) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const errors: Record<string, string> = {};
    for (const key of ['name', 'email', 'phone', 'service', 'message']) {
      errors[key] = validate(key, (formData as any)[key]);
    }
    setFieldErrors(errors);
    setTouched({ name: true, email: true, phone: true, service: true, message: true });

    if (Object.values(errors).some(e => e !== '')) return;

    // Honeypot check — if filled, silently "succeed"
    if (formData.company) {
      setFormState('success');
      return;
    }

    setFormState('submitting');

    try {
      const response = await fetch('https://nthg67e6ni.execute-api.us-east-1.amazonaws.com/prod/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setFormState('success');
      } else {
        setFormState('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormState('error');
    }
  };

  const handleSendAnother = () => {
    setFormData({ name: '', email: '', phone: '', service: '', message: '', company: '' });
    setFieldErrors({});
    setTouched({});
    setFormState('idle');
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_META["/contact"].title}</title>
        <meta name="description" content={PAGE_META["/contact"].description} />
        <meta property="og:title" content={PAGE_META["/contact"].title} />
        <meta property="og:description" content={PAGE_META["/contact"].description} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={BUSINESS.displayName} />
        <link rel="canonical" href={`${BUSINESS.siteUrl}/contact`} />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: '#1e3a8a',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.875rem', md: '3.5rem' },
                color: 'white',
              }}
            >
              Contact MAD Title Agency
            </Typography>
            <Typography
              variant="h5"
              sx={{
                maxWidth: 800,
                mx: 'auto',
                opacity: 0.9,
                color: '#94a3b8',
              }}
            >
              Ready to start your title process? Get in touch with our expert team for fast, reliable service.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                {formState === 'success' ? (
                  /* Success state — replaces form */
                  <Box role="status" sx={{ textAlign: 'center', py: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: '#1f2937' }}>
                      Message Sent!
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, color: '#4b5563' }}>
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleSendAnother}
                      sx={{ borderColor: '#1e3a8a', color: '#1e3a8a' }}
                    >
                      Send Another Message
                    </Button>
                  </Box>
                ) : (
                  /* Form states: idle, submitting, error */
                  <>
                    <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600, color: '#64748b' }}>
                      Get Your Free Quote
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                      Fill out the form below and we'll get back to you within 24 hours with a personalized quote for your title services.
                    </Typography>

                    {formState === 'error' && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        Something went wrong — please call {BUSINESS.contact.phoneDisplay} or try again.
                      </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                      {/* Honeypot — hidden from real users */}
                      <TextField
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        sx={{ position: 'absolute', left: '-9999px' }}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                      />
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="contact-name"
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            variant="outlined"
                            error={!!fieldErrors.name}
                            helperText={fieldErrors.name}
                            disabled={formState === 'submitting'}
                            inputProps={{ 'aria-describedby': fieldErrors.name ? 'name-error' : undefined }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="contact-email"
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            variant="outlined"
                            error={!!fieldErrors.email}
                            helperText={fieldErrors.email}
                            disabled={formState === 'submitting'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="contact-phone"
                            label="Phone Number"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            variant="outlined"
                            error={!!fieldErrors.phone}
                            helperText={fieldErrors.phone}
                            disabled={formState === 'submitting'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            id="contact-service"
                            label="Service Needed"
                            name="service"
                            select
                            value={formData.service}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            variant="outlined"
                            error={!!fieldErrors.service}
                            helperText={fieldErrors.service}
                            disabled={formState === 'submitting'}
                          >
                            <MenuItem value="" disabled>Select a service</MenuItem>
                            {SERVICE_OPTIONS.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            id="contact-message"
                            label="Message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            multiline
                            rows={6}
                            variant="outlined"
                            placeholder="Please provide details about your property, transaction type, and any specific requirements..."
                            error={!!fieldErrors.message}
                            helperText={fieldErrors.message}
                            disabled={formState === 'submitting'}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={formState === 'submitting'}
                            aria-busy={formState === 'submitting'}
                            startIcon={formState === 'submitting' ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            sx={{
                              px: 4,
                              py: 1.5,
                            }}
                          >
                            {formState === 'submitting' ? 'Sending\u2026' : 'Send Message'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600, color: '#1f2937' }}>
                  Contact Information
                </Typography>

                <List>
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ backgroundColor: '#f59e0b' }}>
                        <PhoneIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Phone"
                      secondary={
                        <Box>
                          <Typography variant="body2" component="div">
                            <strong>{BUSINESS.contact.phoneDisplay}</strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Mon-Fri: 9AM-5PM
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>

                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ backgroundColor: '#f59e0b' }}>
                        <EmailIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={
                        <Typography variant="body2" component="div">
                          <strong>{BUSINESS.contact.email}</strong>
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ backgroundColor: '#f59e0b' }}>
                        <LocationIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Location"
                      secondary={
                        <Box>
                          <Typography variant="body2" component="div">
                            <strong>{BUSINESS.address.street}</strong>
                          </Typography>
                          <Typography variant="body2" component="div">
                            <strong>{BUSINESS.address.city}, {BUSINESS.address.state} {BUSINESS.address.zip}</strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Serving Middlesex & Warren Counties
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>

                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ backgroundColor: '#f59e0b' }}>
                        <ScheduleIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Business Hours"
                      secondary={
                        <Box>
                          <Typography variant="body2" component="div">
                            <strong>Monday - Friday:</strong> {BUSINESS.hours.monFri}
                          </Typography>
                          <Typography variant="body2" component="div">
                            <strong>Saturday:</strong> {BUSINESS.hours.sat}
                          </Typography>
                          <Typography variant="body2" component="div">
                            <strong>Sunday:</strong> {BUSINESS.hours.sun}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card sx={{ mt: 3 }}>
              <CardContent sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1f2937' }}>
                  Emergency Closings
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
                  Need a same-day or weekend closing? We offer emergency services for urgent situations.
                </Typography>
                <Button
                  variant="outlined"
                  href={`tel:${BUSINESS.contact.phone}`}
                  sx={{
                    borderColor: '#1e3a8a',
                    color: '#1e3a8a',
                    '&:hover': {
                      backgroundColor: 'rgba(30, 58, 138, 0.1)',
                    },
                  }}
                >
                  Call Emergency Line
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Service Area Details */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#1f2937', fontWeight: 600 }}>
              Comprehensive Service Area Coverage
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
              Professional title services throughout Eastern New Jersey with specialized local expertise
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', p: 4, border: '1px solid #e2e8f0' }}>
                <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                  Primary Service Counties
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  <strong>Middlesex County:</strong> Woodbridge Township, Edison, New Brunswick, Perth Amboy, 
                  Carteret, Old Bridge, Sayreville, South Plainfield, Metuchen, Piscataway, Highland Park, 
                  and all surrounding communities.
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  <strong>Warren County:</strong> Washington Borough, Belvidere, Hackettstown,
                  Blairstown, Hope, and throughout the Delaware River region.
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  <strong>Extended Coverage:</strong> Bergen, Essex, Union, Monmouth, and Somerset counties 
                  with full title services and local municipal knowledge.
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                  Don't see your location? Contact us - we likely serve your area through our network of licensed professionals.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', p: 4, border: '1px solid #e2e8f0' }}>
                <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                  Response Times & Availability
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  <strong>Initial Response:</strong> All inquiries receive a response within 2 business hours during 
                  normal business hours, often much sooner. Emergency closings are handled immediately.
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  <strong>Title Search Timeline:</strong> Most searches completed within 24-48 hours. Rush orders 
                  available for same-day turnaround when needed.
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  <strong>Closing Scheduling:</strong> Flexible scheduling including early morning, evening, and 
                  weekend appointments to accommodate your needs.
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                  <strong>After-Hours Contact:</strong> Emergency line available for urgent title issues or 
                  last-minute closing requirements.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact FAQ */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#1f2937', fontWeight: 600 }}>
            Contact Information & FAQ
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
            Common questions about reaching our team and getting started with your title services
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {[
            {
              question: "What information should I provide when requesting a quote?",
              answer: "Please include the property address, sale price or loan amount, buyer/seller names, estimated closing date, and type of transaction (purchase, refinance, etc.). The more details you provide, the more accurate quote we can prepare."
            },
            {
              question: "How quickly can you start a title search?",
              answer: "We can typically begin title searches within hours of receiving a complete order. Rush orders for same-day searches are available for urgent closings, though we recommend allowing 24-48 hours for thorough examination."
            },
            {
              question: "Do you handle closings outside normal business hours?",
              answer: "Yes! We offer flexible scheduling including early morning, evening, and weekend closings when needed. There may be additional fees for after-hours services, which we'll discuss upfront."
            },
            {
              question: "Can you handle transactions for properties outside your immediate area?",
              answer: "Absolutely. While we're based in Woodbridge, we provide title services throughout Eastern New Jersey. We have extensive knowledge of recording procedures and requirements across multiple counties."
            },
            {
              question: "What's the best way to reach you for urgent matters?",
              answer: `For urgent matters, call our main line at ${BUSINESS.contact.phoneDisplay}. During business hours, you'll speak directly with our team. For after-hours emergencies, leave a detailed message and we'll respond promptly.`
            },
            {
              question: "Do you provide services in languages other than English?",
              answer: "While our primary business is conducted in English, we work with certified translators and bilingual professionals when needed to ensure all parties fully understand the title process and closing procedures."
            }
          ].map((faq, index) => (
            <Grid item xs={12} key={index}>
              <Paper
                elevation={1}
                sx={{
                  p: 4,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(30, 58, 138, 0.08)',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    mb: 2,
                    color: '#1e3a8a',
                    fontWeight: 600,
                  }}
                >
                  {faq.question}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.7,
                    color: '#374151',
                  }}
                >
                  {faq.answer}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Location Details */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#1f2937', fontWeight: 600 }}>
              Visit Our Woodbridge Office
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
              Conveniently located in the heart of Middlesex County with easy access from major highways
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                Office Location & Access
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                <strong>Address:</strong> {BUSINESS.address.street}, {BUSINESS.address.city}, {BUSINESS.address.state} {BUSINESS.address.zip}
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                <strong>Parking:</strong> Free on-site parking available for clients. Additional street parking 
                on Amboy Avenue and surrounding streets.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                <strong>Public Transportation:</strong> Accessible via NJ Transit bus routes with stops on 
                Amboy Avenue. Woodbridge Train Station is approximately 2 miles away.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                <strong>Highway Access:</strong> Convenient to Garden State Parkway (Exit 127), Route 1, 
                Route 35, and Route 9. Easy access from Staten Island via Outerbridge Crossing.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151' }}>
                <strong>Accessibility:</strong> Wheelchair accessible entrance and facilities. Please let us 
                know if you have any special accommodation needs.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 4,
                  height: 'fit-content',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: 'white',
                }}
              >
                <Typography variant="h5" component="h4" sx={{ mb: 3, fontWeight: 600 }}>
                  Directions from Major Areas
                </Typography>
                <List>
                  {[
                    { from: "From Edison", directions: "Take Route 1 North to Amboy Avenue exit, turn right" },
                    { from: "From New Brunswick", directions: "Route 18 South to Route 35 South, right on Amboy Avenue" },
                    { from: "From Perth Amboy", directions: "Route 35 North to Amboy Avenue, turn left" },
                    { from: "From Garden State Parkway", directions: "Exit 127 to Route 35 North, right on Amboy Avenue" },
                    { from: "From Staten Island", directions: "Outerbridge Crossing to Route 35 North, right on Amboy Avenue" }
                  ].map((direction, index) => (
                    <ListItem key={index} sx={{ py: 1, alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {direction.from}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {direction.directions}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Contact; 